import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getRecallClusters, type ViewportBounds } from '@vehix/portal/services/spatialClustering';
import type { DealerLocation } from './useViewportRecallsForZoneMap';
import { getCache, setCache, removeCache } from '@vehix/portal/lib/utils/cache';
import { getTenantIdFromSession } from '@vehix/portal/lib/supabase/tenantScoping';
import { getProdSupabaseClient } from '@vehix/shared/admin/portal-bridge';

// Debug flag for cache operations (auto-enabled in dev mode)
const DEBUG_CACHE = import.meta.env.DEV || import.meta.env.VITE_DEBUG_CACHE === 'true';

// Cache key prefix for zone map recall data
const CACHE_KEY_PREFIX = 'zone_map_recall_clusters';

// Query keys for recall data caching (include selectedOem so cache is per-OEM)
export const recallDataKeys = {
  all: ['recallData'] as const,
  viewport: (
    bounds: ViewportBounds,
    userId?: string,
    snapshotDate?: string | null,
    selectedOem?: string
  ) =>
    [
      ...recallDataKeys.all,
      'viewport',
      {
        bounds: {
          north: Math.round(bounds.north * 1000) / 1000, // Round to 3 decimal places for consistent keys
          south: Math.round(bounds.south * 1000) / 1000,
          east: Math.round(bounds.east * 1000) / 1000,
          west: Math.round(bounds.west * 1000) / 1000
        },
        userId,
        snapshotDate,
        selectedOem
      }
    ] as const,
  allData: (userId?: string, snapshotDate?: string | null, selectedOem?: string) =>
    [...recallDataKeys.all, 'all', { userId, snapshotDate, selectedOem }] as const
};

/**
 * Generate cache key for viewport bounds (regular tenants). Include selectedOem so cache is per-OEM.
 */
function getViewportCacheKey(
  bounds: ViewportBounds,
  snapshotDate?: string | null,
  selectedOem?: string
): string {
  const boundsKey = `${bounds.north.toFixed(3)}_${bounds.south.toFixed(3)}_${bounds.east.toFixed(3)}_${bounds.west.toFixed(3)}`;
  const dateKey = snapshotDate ? `_${snapshotDate}` : '';
  const oemKey = selectedOem ? `_${selectedOem}` : '';
  return `${CACHE_KEY_PREFIX}_${boundsKey}${dateKey}${oemKey}`;
}

/**
 * Generate cache key for all data (internal tenants). Include selectedOem so cache is per-OEM.
 */
function getAllDataCacheKey(snapshotDate?: string | null, selectedOem?: string): string {
  const dateKey = snapshotDate ? `_${snapshotDate}` : '';
  const oemKey = selectedOem ? `_${selectedOem}` : '';
  return `${CACHE_KEY_PREFIX}_all${dateKey}${oemKey}`;
}

/**
 * Filter dealer locations by viewport bounds (client-side)
 */
function filterDealersByBounds(
  dealers: DealerLocation[],
  bounds: ViewportBounds
): DealerLocation[] {
  return dealers.filter(dealer => {
    const lat = dealer.coordinates.latitude;
    const lon = dealer.coordinates.longitude;
    return lat >= bounds.south && lat <= bounds.north && lon >= bounds.west && lon <= bounds.east;
  });
}

/**
 * Get entire US bounds for loading all data (internal tenants)
 */
function getUSBounds(): ViewportBounds {
  return {
    north: 49.5,
    south: 24.0,
    east: -66.0,
    west: -125.0
  };
}

/**
 * Check if tenant is internal
 */
async function checkInternalTenant(tenantId: string | null): Promise<boolean> {
  if (!tenantId) return false;

  try {
    const prodClient = getProdSupabaseClient();
    const { data: tenantData } = await prodClient
      .from('tenants')
      .select('tenant_type')
      .eq('id', tenantId)
      .maybeSingle();

    return tenantData?.tenant_type === 'internal';
  } catch (err) {
    console.warn('[useCachedRecallData] Failed to check tenant type:', err);
    return false;
  }
}

/**
 * Hook to fetch recall data for a viewport with localStorage persistence
 * For internal tenants: Loads all data once and filters client-side by viewport
 * For regular tenants: Uses viewport-based caching
 * Cache duration: 24 hours since recall data doesn't change frequently
 */
export function useCachedRecallData(
  bounds: ViewportBounds | null,
  userId?: string,
  snapshotDate?: string | null,
  tenantId?: string | null,
  selectedOem?: string
) {
  const [isLoadingFromCache, setIsLoadingFromCache] = React.useState(false);
  const [cachedData, setCachedData] = React.useState<DealerLocation[] | null>(null);
  const [isInternalTenant, setIsInternalTenant] = React.useState<boolean>(false);
  const [allDataCache, setAllDataCache] = React.useState<DealerLocation[] | null>(null);

  // Check if tenant is internal
  React.useEffect(() => {
    const checkTenant = async () => {
      if (!tenantId) {
        setIsInternalTenant(false);
        return;
      }
      const isInternal = await checkInternalTenant(tenantId);
      setIsInternalTenant(isInternal);
    };
    checkTenant();
  }, [tenantId]);

  // For internal tenants: Load all data from cache (not viewport-specific)
  React.useEffect(() => {
    const loadAllDataFromCache = async () => {
      if (!isInternalTenant || !tenantId) {
        setAllDataCache(null);
        return;
      }

      try {
        const cacheKey = getAllDataCacheKey(snapshotDate, selectedOem);
        const cached = await getCache<DealerLocation[]>(cacheKey, {
          tenantId,
          debug: DEBUG_CACHE
        });

        if (cached && cached.length > 0) {
          if (DEBUG_CACHE) {
            console.log(
              `[useCachedRecallData] ✅ Loaded ${cached.length} dealer locations (all data) from localStorage cache (internal tenant)`
            );
          }
          setAllDataCache(cached);
        } else {
          if (DEBUG_CACHE) {
            console.log('[useCachedRecallData] No all-data cache found (internal tenant)');
          }
          setAllDataCache(null);
        }
      } catch (error) {
        if (DEBUG_CACHE) {
          console.warn('[useCachedRecallData] Error loading all-data cache:', error);
        }
        setAllDataCache(null);
      }
    };

    loadAllDataFromCache();
  }, [isInternalTenant, snapshotDate, tenantId, selectedOem]);

  // Note: Client-side filtering is now done directly in activeData calculation
  // This ensures we always use the freshest data (query data preferred over cache)

  // For regular tenants: Load from localStorage cache by viewport (existing behavior)
  React.useEffect(() => {
    const loadFromCache = async () => {
      if (isInternalTenant || !bounds || !tenantId) {
        if (!isInternalTenant) {
          setCachedData(null);
        }
        setIsLoadingFromCache(false);
        return;
      }

      try {
        setIsLoadingFromCache(true);
        const cacheKey = getViewportCacheKey(bounds, snapshotDate, selectedOem);
        const cached = await getCache<DealerLocation[]>(cacheKey, {
          tenantId,
          debug: DEBUG_CACHE
        });

        if (cached && cached.length > 0) {
          if (DEBUG_CACHE) {
            console.log(
              `[useCachedRecallData] ✅ Loaded ${cached.length} dealer locations from localStorage cache (regular tenant)`
            );
          }
          setCachedData(cached);
        } else {
          if (DEBUG_CACHE) {
            console.log('[useCachedRecallData] No cache found for viewport (regular tenant)');
          }
          setCachedData(null);
        }
      } catch (error) {
        if (DEBUG_CACHE) {
          console.warn('[useCachedRecallData] Error loading from cache:', error);
        }
        setCachedData(null);
      } finally {
        setIsLoadingFromCache(false);
      }
    };

    loadFromCache();
  }, [bounds, snapshotDate, tenantId, isInternalTenant, selectedOem]);

  // For internal tenants: Query all data with US-wide bounds
  const allDataQuery = useQuery({
    queryKey: recallDataKeys.allData(userId, snapshotDate, selectedOem),
    queryFn: async (): Promise<DealerLocation[]> => {
      if (!isInternalTenant) return [];

      const usBounds = getUSBounds();
      console.log(
        '📡 [Internal Tenant] Fetching ALL recall data for US bounds, snapshot:',
        snapshotDate,
        'oem:',
        selectedOem
      );

      // Use server-side clustering for stable data (OEM-filtered)
      const clusters = await getRecallClusters(usBounds, 10, userId, {
        snapshotDate,
        oem: selectedOem ?? 'Ford'
      });

      // Convert server-side clusters back to individual dealer locations
      const dealers: DealerLocation[] = clusters.map(cluster => {
        const sampleDealerInfo =
          Array.isArray(cluster.sampleDealers) && cluster.sampleDealers.length > 0
            ? cluster.sampleDealers[0]
            : {};
        const dealerName = sampleDealerInfo.name || cluster.dealerNames?.[0] || 'Unknown Dealer';

        return {
          dealerId: cluster.clusterId,
          dealerName: dealerName,
          address: sampleDealerInfo.address || 'N/A',
          city: sampleDealerInfo.city || '',
          state: sampleDealerInfo.state || '',
          zip: sampleDealerInfo.zip || '',
          phone: sampleDealerInfo.phone || null,
          coordinates: {
            latitude: cluster.centerLat,
            longitude: cluster.centerLon
          },
          vehicleCount: cluster.vehicleCount,
          recallCount: cluster.recallCount,
          campaignCount: 0,
          highestSkillLevel: null,
          makes: cluster.makes || [], // Unique makes for OEM filtering
          vehicles: []
        };
      });

      // Cache all data to localStorage for persistence (internal tenants)
      if (tenantId && dealers.length > 0) {
        const cacheKey = getAllDataCacheKey(snapshotDate, selectedOem);
        await setCache(cacheKey, dealers, {
          tenantId,
          ttl: 24 * 60 * 60 * 1000, // 24 hours
          compress: true,
          debug: DEBUG_CACHE
        });
      }

      // Debug: Verify total recall count matches server calculation
      const totalRecallsFromDealers = dealers.reduce(
        (sum, dealer) => sum + (dealer.recallCount || 0),
        0
      );
      console.log(
        `💾 [Internal Tenant] Cached ${dealers.length} dealer locations (all data) with ${totalRecallsFromDealers} total recalls (sum of dealer counts)`
      );
      return dealers;
    },
    enabled: isInternalTenant && !!tenantId && !!selectedOem,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 45 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000)
  });

  // For regular tenants: Query by viewport (existing behavior)
  const viewportQuery = useQuery({
    queryKey: bounds
      ? recallDataKeys.viewport(bounds, userId, snapshotDate, selectedOem)
      : ['recallData', 'empty'],
    queryFn: async (): Promise<DealerLocation[]> => {
      if (!bounds || isInternalTenant) return [];

      console.log(
        '📡 [Regular Tenant] Fetching recall data for bounds:',
        bounds,
        'snapshot:',
        snapshotDate,
        'oem:',
        selectedOem
      );

      // Use server-side clustering for stable data (OEM-filtered)
      const clusters = await getRecallClusters(bounds, 10, userId, {
        snapshotDate,
        oem: selectedOem ?? 'Ford'
      });

      // Convert server-side clusters back to individual dealer locations
      const dealers: DealerLocation[] = clusters.map(cluster => {
        const sampleDealerInfo =
          Array.isArray(cluster.sampleDealers) && cluster.sampleDealers.length > 0
            ? cluster.sampleDealers[0]
            : {};
        const dealerName = sampleDealerInfo.name || cluster.dealerNames?.[0] || 'Unknown Dealer';

        return {
          dealerId: cluster.clusterId,
          dealerName: dealerName,
          address: sampleDealerInfo.address || 'N/A',
          city: sampleDealerInfo.city || '',
          state: sampleDealerInfo.state || '',
          zip: sampleDealerInfo.zip || '',
          phone: sampleDealerInfo.phone || null,
          coordinates: {
            latitude: cluster.centerLat,
            longitude: cluster.centerLon
          },
          vehicleCount: cluster.vehicleCount,
          recallCount: cluster.recallCount,
          campaignCount: 0,
          highestSkillLevel: null,
          makes: cluster.makes || [], // Unique makes for OEM filtering
          vehicles: []
        };
      });

      // Cache to localStorage for persistence (regular tenants)
      if (tenantId && dealers.length > 0) {
        const cacheKey = getViewportCacheKey(bounds, snapshotDate, selectedOem);
        await setCache(cacheKey, dealers, {
          tenantId,
          ttl: 24 * 60 * 60 * 1000, // 24 hours
          compress: true,
          debug: DEBUG_CACHE
        });
      }

      // Debug: Verify total recall count
      const totalRecallsFromDealers = dealers.reduce(
        (sum, dealer) => sum + (dealer.recallCount || 0),
        0
      );
      console.log(
        `💾 [Regular Tenant] Cached ${dealers.length} dealer locations for viewport with ${totalRecallsFromDealers} total recalls (sum of dealer counts)`
      );
      return dealers;
    },
    enabled: !!bounds && !!tenantId && !isInternalTenant && !!selectedOem,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    gcTime: 45 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    retry: 3,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
    initialData: cachedData || undefined,
    placeholderData: cachedData || undefined
  });

  // For internal tenants: Update all-data cache when query completes
  React.useEffect(() => {
    if (isInternalTenant && allDataQuery.data) {
      setAllDataCache(allDataQuery.data);
    }
  }, [isInternalTenant, allDataQuery.data]);

  // Determine which query result to use
  // CRITICAL: Always prefer fresh query data over cached data to ensure accurate recall counts
  // Fresh query data has correct counts (unique vin+campaign_number pairs), cached data might have old counts
  const activeQuery = isInternalTenant ? allDataQuery : viewportQuery;
  const dataSource = isInternalTenant
    ? allDataQuery.data || allDataCache // Prefer fresh query data (has correct counts)
    : activeQuery.data || cachedData; // Regular tenants: prefer fresh query data

  const activeData =
    isInternalTenant && bounds && dataSource
      ? filterDealersByBounds(dataSource, bounds)
      : dataSource || [];

  const queryClient = useQueryClient();

  // Refresh function: Clear cache and refetch data
  const refreshData = React.useCallback(async () => {
    if (!tenantId) {
      console.warn('[useCachedRecallData] Cannot refresh: tenantId not available');
      return;
    }

    try {
      // Clear localStorage cache entries for this tenant
      if (isInternalTenant) {
        const allDataKey = getAllDataCacheKey(snapshotDate, selectedOem);
        removeCache(allDataKey, tenantId, DEBUG_CACHE);

        // Invalidate React Query cache for all data
        queryClient.invalidateQueries({
          queryKey: recallDataKeys.allData(userId, snapshotDate, selectedOem)
        });
      } else {
        // For regular tenants, clear the viewport cache
        if (bounds) {
          const viewportKey = getViewportCacheKey(bounds, snapshotDate, selectedOem);
          removeCache(viewportKey, tenantId, DEBUG_CACHE);

          // Invalidate React Query cache for viewport data
          queryClient.invalidateQueries({
            queryKey: recallDataKeys.viewport(bounds, userId, snapshotDate, selectedOem)
          });
        }
      }

      if (DEBUG_CACHE) {
        console.log('[useCachedRecallData] Cache cleared, React Query will refetch automatically');
      }
    } catch (error) {
      console.error('[useCachedRecallData] Error refreshing data:', error);
    }
  }, [tenantId, isInternalTenant, snapshotDate, bounds, userId, queryClient, selectedOem]);

  // Debug logging to verify data source and counts
  React.useEffect(() => {
    if (DEBUG_CACHE && activeData.length > 0) {
      const totalRecalls = activeData.reduce((sum, dealer) => sum + (dealer.recallCount || 0), 0);
      const source = isInternalTenant
        ? allDataQuery.data
          ? 'FRESH QUERY DATA'
          : 'CACHED DATA'
        : activeQuery.data
          ? 'FRESH QUERY DATA'
          : 'CACHED DATA';

      // For internal tenants, also log the total across all dealers (not just viewport)
      if (isInternalTenant && dataSource) {
        const totalAcrossAllDealers = dataSource.reduce(
          (sum, dealer) => sum + (dealer.recallCount || 0),
          0
        );
        console.log(
          `[useCachedRecallData] Viewport: ${activeData.length} dealers, ${totalRecalls} recalls | All dealers: ${dataSource.length} dealers, ${totalAcrossAllDealers} recalls | Source: ${source}`
        );
      } else {
        console.log(
          `[useCachedRecallData] Viewport: ${activeData.length} dealers, ${totalRecalls} total recalls | Source: ${source}`
        );
      }
    }
  }, [activeData, isInternalTenant, allDataQuery.data, activeQuery.data, dataSource]);

  // Return query with loading state that accounts for cache loading
  return {
    ...activeQuery,
    data: activeData,
    isLoading: activeQuery.isLoading && !isLoadingFromCache && !dataSource,
    isLoadingFromCache,
    refreshData
  };
}

/**
 * Helper hook that mimics the original interface for easier migration
 * Now includes localStorage persistence via cache utility
 * For internal tenants: Loads all data once and filters client-side (no reload on zoom)
 * Recall counts are always for the selected OEM only (required).
 */
export function useStableDealerClusteringCached(
  userId?: string,
  snapshotDate?: string | null,
  selectedOem?: string
) {
  const [currentBounds, setCurrentBounds] = React.useState<ViewportBounds | null>(null);
  const [tenantId, setTenantId] = React.useState<string | null>(null);

  // Get tenantId for cache operations
  React.useEffect(() => {
    const loadTenantId = async () => {
      try {
        const prodClient = getProdSupabaseClient();
        const id = await getTenantIdFromSession(prodClient);
        setTenantId(id);
      } catch (error) {
        if (DEBUG_CACHE) {
          console.warn('[useStableDealerClusteringCached] Failed to get tenantId:', error);
        }
        setTenantId(null);
      }
    };

    loadTenantId();
  }, []);

  const {
    data: dealerLocations = [],
    isLoading: loading,
    error,
    refetch,
    isLoadingFromCache,
    refreshData
  } = useCachedRecallData(currentBounds, userId, snapshotDate, tenantId, selectedOem);

  const loadViewportData = React.useCallback(async (bounds: ViewportBounds) => {
    console.log('🗺️ Loading viewport data for bounds:', bounds);
    setCurrentBounds(bounds);
    // The query will automatically trigger when currentBounds changes
    // For internal tenants, this just filters client-side (no database query)
  }, []);

  // Calculate zone stats from cached data
  const calculateZoneStats = React.useCallback(
    async (zipCodes: string[]) => {
      if (zipCodes.length === 0 || dealerLocations.length === 0) {
        return { totalRecalls: 0, uniqueVins: 0 };
      }

      const zipSet = new Set(zipCodes);
      let totalRecalls = 0;
      let estimatedVins = 0;

      dealerLocations.forEach(dealer => {
        if (zipSet.has(dealer.zip)) {
          totalRecalls += dealer.recallCount;
          estimatedVins += dealer.vehicleCount;
        }
      });

      return {
        totalRecalls,
        uniqueVins: estimatedVins
      };
    },
    [dealerLocations]
  );

  return {
    dealerLocations,
    loading: loading || isLoadingFromCache,
    error: error?.message || null,
    loadViewportData,
    calculateZoneStats,
    currentBounds,
    refetch, // Added for manual refresh if needed
    refreshData, // Clear cache and refresh data
    isLoadingFromCache // Expose cache loading state
  };
}
